import React from 'react';
import usePatternMemory from '../../hooks/usePatternMemory';
import { fetchTodayCheckin, fetchHormoneData } from '../../lib/userDataService';
import { useEffect, useState } from 'react';

/**
 * SmartSuggestionsScreen — the intelligence engine.
 *
 * Suggestions are generated by combining:
 *   - phase (from useCycleData → props)
 *   - today's checkin (energy/mood/pain/symptoms JSONB)
 *   - hormone reports (presence + LH-to-FSH heuristic)
 *   - pattern memory (observations from last 90 days)
 *
 * Every card includes a "why" line — the concrete evidence Theo used —
 * so the user never sees generic wellness advice.
 */

const CATEGORIES = [
  { key: 'nutrition', label: 'Nutrition' },
  { key: 'exercise',  label: 'Movement'  },
  { key: 'sleep',     label: 'Sleep'     },
  { key: 'stress',    label: 'Stress'    },
  { key: 'hydration', label: 'Hydration' },
  { key: 'workload',  label: 'Workload'  },
];

function generate({ phase, dayNumber, waiting, checkin, hormones, observations }) {
  const out = [];
  const energy = checkin?.energy_level;
  const pain   = checkin?.pain_level;
  const syms   = checkin?.symptoms || {};

  // --- Recovery-driven ---
  const recoveryDown = observations.find(o => o.key === 'recovery_down');
  const recoveryUp   = observations.find(o => o.key === 'recovery_up');
  if (recoveryDown) {
    out.push({ cat: 'sleep', tone: 'warn', title: 'Protect sleep tonight',
      body: 'Aim for a 30-minute earlier wind-down. Dim screens after 9pm.',
      why: recoveryDown.evidence });
    out.push({ cat: 'workload', tone: 'warn', title: 'Lighten today\u2019s schedule',
      body: 'Push non-urgent meetings by a day. Say yes to less.',
      why: recoveryDown.evidence });
  }
  if (recoveryUp) {
    out.push({ cat: 'exercise', tone: 'brand', title: 'Add one moderate session',
      body: 'Your body is recovering well — a 25-minute walk or strength session lands well today.',
      why: recoveryUp.evidence });
  }

  // --- Symptom recurrence ---
  const recur = observations.find(o => o.key === 'symptom_recurrence');
  if (recur) {
    out.push({ cat: 'nutrition', tone: 'brand', title: 'Reduce inflammation triggers',
      body: 'Prioritise omega-3s and cut refined sugar for the next 5 days.',
      why: recur.evidence });
  }

  // --- Phase-based (never generic — always paired with a why) ---
  if (phase === 'menstrual') {
    out.push({ cat: 'nutrition', tone: 'brand', title: 'Iron-rich meals this week',
      body: 'Lentils, spinach and lean meat pair well with vitamin-C for absorption.',
      why: `You are on day ${dayNumber} of your period — iron stores are lower.` });
    out.push({ cat: 'exercise', tone: 'quiet', title: 'Yin over intensity',
      body: 'Gentle yoga, walking, mobility. Skip max-effort lifts for two days.',
      why: 'Cortisol response to hard training is elevated during menstruation.' });
  }
  if (phase === 'follicular') {
    out.push({ cat: 'exercise', tone: 'brand', title: 'This is your build window',
      body: 'Estrogen is climbing — schedule your harder sessions this week.',
      why: `Day ${dayNumber} · Estrogen is rising, which supports strength gains and faster recovery.` });
  }
  if (phase === 'ovulatory') {
    out.push({ cat: 'nutrition', tone: 'brand', title: 'Hydrate + electrolytes',
      body: 'Cervical mucus and mid-cycle heat mean higher fluid needs.',
      why: `Ovulation is likely today or in the next 48 hours (day ${dayNumber}).` });
  }
  if (phase === 'luteal') {
    out.push({ cat: 'stress', tone: 'brand', title: 'Buffer emotional triggers',
      body: 'Progesterone amplifies stress reactivity — schedule a 20-min walk after emotional calls.',
      why: `You are on day ${dayNumber} — the luteal phase where mood shifts are physiological, not personal.` });
    out.push({ cat: 'nutrition', tone: 'quiet', title: 'Complex carbs > sugar spikes',
      body: 'Oats, sweet potato and berries steady your energy without the crash.',
      why: 'Cravings peak in luteal — refined sugar amplifies the mood swing pattern.' });
  }
  if (waiting) {
    out.push({ cat: 'stress', tone: 'warn', title: 'Delayed cycle → gentle nervous system work',
      body: '5 minutes of box-breathing twice today. Skip caffeine after 2pm.',
      why: 'When ovulation is delayed with PCOS, elevated cortisol tends to compound the delay.' });
  }

  // --- Live checkin signals ---
  if (energy != null && energy <= 3) {
    out.push({ cat: 'workload', tone: 'warn', title: 'Halve your to-do list today',
      body: 'Ship one thing that matters. Everything else can wait.',
      why: `Your energy today is ${energy}/10 — your body is asking for less input.` });
  }
  if (pain != null && pain >= 6) {
    out.push({ cat: 'exercise', tone: 'warn', title: 'Rest, don\u2019t train',
      body: 'Warm heat, hydration, and elevation. Save intensity for tomorrow.',
      why: `You logged pain at ${pain}/10 today.` });
  }
  if (Number(syms.cramps) >= 2) {
    out.push({ cat: 'nutrition', tone: 'brand', title: 'Magnesium tonight',
      body: '300–400mg magnesium glycinate has evidence for cramp relief.',
      why: 'You logged cramps at moderate-or-higher intensity today.' });
  }

  // --- Hormone-driven ---
  if (hormones?.lh && hormones?.fsh && hormones.lh / hormones.fsh >= 2.5) {
    out.push({ cat: 'nutrition', tone: 'brand', title: 'Inositol may help',
      body: 'Discuss myo-inositol + D-chiro-inositol with your clinician — evidence-backed for high LH:FSH.',
      why: `Your last panel shows LH ${hormones.lh} / FSH ${hormones.fsh} — ratio elevated (typical PCOS pattern).` });
  }

  // Guarantee a minimum of 3 suggestions
  if (out.length < 3) {
    out.push({ cat: 'hydration', tone: 'quiet', title: 'Log water today',
      body: 'We\u2019ll build hydration into your pattern detection from your logs.',
      why: 'Not enough data yet to personalise this — start with today.' });
  }

  return out;
}

export default function SmartSuggestionsScreen({ userId, phase, dayNumber, waiting }) {
  const [checkin, setCheckin] = useState(null);
  const [hormones, setHormones] = useState(null);
  const [loading, setLoading] = useState(true);
  const { observations, loading: loadingMemory } = usePatternMemory(userId);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userId) { if (alive) setLoading(false); return; }
      const [c, h] = await Promise.all([fetchTodayCheckin(userId), fetchHormoneData(userId)]);
      if (!alive) return;
      setCheckin(c); setHormones(h); setLoading(false);
    })();
    return () => { alive = false; };
  }, [userId]);

  const busy = loading || loadingMemory;
  const suggestions = busy ? [] : generate({ phase, dayNumber, waiting, checkin, hormones, observations });

  return (
    <div className="cx-sub-body" data-testid="smart-suggestions-screen">
      <section className="cx-intel-hero">
        <div className="cx-intel-eyebrow">{phase.toUpperCase()} · DAY {dayNumber} · Theo</div>
        <h1 className="cx-intel-h1">
          {busy ? 'Reading your patterns\u2026' : suggestions.length > 5 ? 'Six ways to help your body today.' : 'Personalised for today.'}
        </h1>
        <div className="cx-intel-conf-chip" style={{ marginTop: 16 }}>
          <span className="cx-conf-dot" />
          {busy ? 'Analysing' : `${suggestions.length} suggestion${suggestions.length === 1 ? '' : 's'} · every one has a why`}
        </div>
      </section>

      <section className="cx-intel-section">
        <div className="cx-intel-label">Today&rsquo;s recommendations</div>
        <div className="cx-suggest-list" data-testid="suggestions-list">
          {suggestions.map((s, i) => (
            <SuggestionCard key={i} s={s}/>
          ))}
        </div>
      </section>

      {!busy && observations.length > 0 && (
        <section className="cx-intel-section">
          <div className="cx-intel-label">Why Theo is saying this</div>
          <div className="cx-suggest-obs">
            {observations.slice(0, 3).map((o) => (
              <div key={o.key} className={`cx-suggest-obs-row cx-suggest-obs-row--${o.tone || 'brand'}`} data-testid={`obs-${o.key}`}>
                <div className="cx-obs-title">{o.headline}</div>
                <div className="cx-obs-evidence">{o.evidence}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="cx-intel-section">
        <p className="cx-fine-print">
          Suggestions are personalised from your logs, cycle history, and (when available) hormone reports.
          They are not medical advice.
        </p>
      </section>
    </div>
  );
}

function SuggestionCard({ s }) {
  const catLabel = CATEGORIES.find(c => c.key === s.cat)?.label || s.cat;
  return (
    <div className={`cx-suggest-card cx-suggest-card--${s.tone || 'brand'}`} data-testid={`sug-${s.cat}`}>
      <div className="cx-suggest-cat">{catLabel}</div>
      <div className="cx-suggest-title">{s.title}</div>
      <div className="cx-suggest-body">{s.body}</div>
      <div className="cx-suggest-why">
        <span className="cx-suggest-why-tag">WHY</span> {s.why}
      </div>
    </div>
  );
}
